/*
	Teléfono de Ventas dentro del navegador (reemplazo de VozMia). Se registra como extensión de la asesora en la central
	propia (Asterisk en sip.washingtonacademy.com) por WSS, con JsSIP (public/vendor, se carga solo al encender el teléfono).
	Un solo teléfono por pestaña: es un objeto único fuera de React; las pantallas se suscriben a sus cambios.
	Cada llamada lleva X-WA-Call (liga la grabación con la bitácora) y X-WA-Prospect.
*/
import { avisoLocal } from "../../../../appMovil/appMovil";
import { frenarRecarga } from "../../../../versionWatcher";

export interface PbxCreds { extension: string; password: string; name: string; domain: string; wss: string; iceServers: any[]; enabled: boolean;
	/** false = entrada compartida de WappTeam: solo marca a las salas, no se registra (no recibe llamadas). */
	registrar?: boolean; }

export type EstadoTel = "apagado" | "conectando" | "listo" | "error";

export interface Llamada {
	callId: string;
	direction: "out" | "in";
	number: string;
	name: string | null;
	prospectId: number | null;
	status: "marcando" | "sonando" | "entrante" | "en_llamada" | "terminada";
	startedAt: number | null;      // cuando contestaron (ms)
	muted: boolean;
	held: boolean;
	consult: { number: string; status: "marcando" | "en_llamada" } | null;   // transferencia con aviso
	motivo?: string | null;        // por qué no se completó (se muestra unos segundos)
	/** Llamada de WappTeam (id de la llamada): la pantalla es la de WappTeam, no el panel de clientes. */
	equipo?: number | null;
}

/**
 * Por qué no se completó una llamada, en palabras (Esteban, 2026-10-09: "pareciera que el sistema falla y es que ciertos
 * números se rechazan"). null = no hace falta avisar (colgó la asesora).
 */
export const motivoFallo = (cause: string | null, code: number | null, originator: string | null): string | null => {
	if (originator === "local" || cause === "Canceled") return null;
	if (code === 403 || code === 401 || code === 407) return "El operador rechazó la llamada a este número.";
	if (code === 404 || code === 484 || code === 604 || cause === "Not Found" || cause === "Address Incomplete") return "El número no existe o está incompleto.";
	if (code === 486 || code === 600 || cause === "Busy") return "Ocupado.";
	if (code === 603 || cause === "Rejected") return "Rechazaron la llamada.";
	if (code === 480 || code === 408 || cause === "Unavailable" || cause === "No Answer" || cause === "Request Timeout") return "No contestan o el teléfono está apagado.";
	if (code === 488 || cause === "Incompatible SDP" || cause === "Bad Media Description") return "La línea no aceptó el audio de la llamada.";
	if (cause === "User Denied Media Access") return "El navegador no dio permiso al micrófono.";
	if (cause === "RTP Timeout") return "Se perdió la conexión de audio.";
	// 5xx (Esteban, 2026-10-13: "mal explicado"): la compañía o la conexión con ella fallaron, no la asesora ni el número.
	if (code === 503) return "La llamada no se pudo conectar por una falla de la línea. Vuelve a marcar.";
	if (code && code >= 500) return "La compañía telefónica no completó la llamada. Vuelve a marcar en unos segundos.";
	if (cause === "Connection Error") return "Se perdió la conexión con la central.";
	return cause ? "No se pudo completar (" + cause + (code ? " " + code : "") + ")." : null;
};

/**
 * Llamada que no contestaron (Esteban, 2026-10-09: "si no contestó, debería ser automáticamente marcado"): se registra
 * sola, sin preguntar. Devuelve el resultado o null si contestaron. Si contestó y colgó en 5 s o menos ya no se registra
 * sola como "No contestó" (Esteban, 2026-10-14): se pregunta, con "Me colgó" ya marcado (resultadoInicial).
 */
export const resultadoSinRespuesta = (f: { answered: boolean; durationSec: number; cause: string | null }): string | null =>
	f.answered ? null : f.cause === "Busy" ? "ocupado" : "no_contesto";

/** Con qué resultado se abre el cierre de una llamada que sí contestaron: "Me colgó" si duró 5 s o menos. */
export const resultadoInicial = (f: { answered: boolean; durationSec: number; cause: string | null }): string =>
	resultadoSinRespuesta(f) || (f.durationSec <= 5 ? "colgo" : "contesto");

export interface FinLlamada { callId: string; prospectId: number | null; number: string; direction: "out" | "in"; durationSec: number; answered: boolean; cause: string | null; campaignContactId?: number | null; equipo?: number | null; }

type Escucha = () => void;
type EscuchaFin = (f: FinLlamada) => void;

const SCRIPT = "/vendor/jssip-3.10.1.min.js";

const cargarJsSIP = (): Promise<any> => new Promise((resolve, reject) => {
	const w = window as any;
	if (w.JsSIP) return resolve(w.JsSIP);
	const s = document.createElement("script");
	s.src = SCRIPT;
	s.async = true;
	s.onload = () => (w.JsSIP ? resolve(w.JsSIP) : reject(new Error("No cargó el teléfono")));
	s.onerror = () => reject(new Error("No cargó el teléfono"));
	document.head.appendChild(s);
});

const uuid = (): string => {
	const c: any = (window as any).crypto;
	if (c?.randomUUID) return c.randomUUID();
	return "c" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
};

/** "+504 3327-7716" → "50433277716" (lo que entiende el plan de marcado). */
export const aMarcable = (e164: string): string => (e164 || "").replace(/\D/g, "");

/**
 * Autocorrector del marcador (Esteban, 2026-10-09): mientras se escribe o se pega, solo quedan dígitos (y * # para menús);
 * guiones, espacios, paréntesis y "+" se descartan sin perder los números. La lada del país se quita sola en cuanto el
 * número está completo: +504 9276-3870 → 92763870, +52 1 81 7540 0500 → 8175400500, 00504… igual.
 */
export const limpiarMarcado = (texto: string): string => {
	let d = (texto || "").replace(/[^\d*#]/g, "");
	if (d.startsWith("00")) d = d.slice(2);
	if (/^504\d{8}$/.test(d)) return d.slice(3);
	if (/^521\d{10}$/.test(d)) return d.slice(3);
	if (/^52\d{10}$/.test(d)) return d.slice(2);
	return d;
};

/** Lo que se manda a la central: 8 dígitos = Honduras, 10 = México, 1 + 10 = EEUU; extensiones y lo demás, tal cual. */
export const aDestino = (texto: string): string => {
	const n = limpiarMarcado(texto);
	return n.length === 8 && /^\d+$/.test(n) ? "504" + n : n.length === 10 && /^\d+$/.test(n) ? "52" + n : n;
};

class Telefono {
	estado: EstadoTel = "apagado";
	error: string | null = null;
	creds: PbxCreds | null = null;
	llamada: Llamada | null = null;
	private ua: any = null;
	private session: any = null;
	private consultSession: any = null;
	private audio: HTMLAudioElement | null = null;
	private escuchas: Set<Escucha> = new Set();
	private finales: Set<EscuchaFin> = new Set();
	private campaignContactId: number | null = null;
	private reintentos = 0;
	private tReintento: any = null;
	/** De dónde salen las credenciales (lo pone el botón con su plantel); el vigilante las vuelve a pedir al reconectar. */
	private fuente: (() => Promise<PbxCreds | null>) | null = null;
	private noListoDesde = 0;
	private ultimoReinicio = 0;
	private vigilando = false;
	private reiniciando: Promise<void> | null = null;
	/** Reconexiones seguidas sin quedar listo: cada una espera el doble (20 s, 40 s… hasta 5 min) para no saturar la central. */
	private fallas = 0;
	/** El servidor dijo que no (extensión apagada, sin extensiones libres…): no se reintenta solo, solo con un clic. */
	private bloqueado = false;
	private avisosPedidos = false;

	suscribir(fn: Escucha): () => void { this.escuchas.add(fn); return () => { this.escuchas.delete(fn); }; }
	alTerminar(fn: EscuchaFin): () => void { this.finales.add(fn); return () => { this.finales.delete(fn); }; }
	private avisar() {
		if (this.estado === "listo") this.noListoDesde = 0; else if (!this.noListoDesde) this.noListoDesde = Date.now();
		this.escuchas.forEach((f) => { try { f(); } catch (e) { /* una pantalla rota no apaga el teléfono */ } });
	}

	/**
	 * Siempre encendido (Esteban, 2026-10-12: "todas deben tener el teléfono encendido all the time"). Ya no hay botón de
	 * apagar: si el teléfono pasa más de 20 s sin estar listo (la central llena, se cayó el internet, la pestaña durmió),
	 * el vigilante tira la conexión y la arma de nuevo con credenciales frescas. Antes un "Conectando" atorado se quedaba
	 * así para siempre y el clic en el botón no hacía nada (Kimberly, TGU: la central topaba en 100 conexiones).
	 */
	usarFuente(fn: () => Promise<PbxCreds | null>) {
		this.fuente = fn;
		if (this.vigilando) return;
		this.vigilando = true;
		window.setInterval(() => {
			if (this.listo || this.ocupado || !this.noListoDesde || this.bloqueado) return;
			const espera = Math.min((this.estado === "error" ? 60000 : 20000) * Math.pow(2, this.fallas), 300000);
			if (Date.now() - this.noListoDesde > espera && Date.now() - this.ultimoReinicio > espera) this.reiniciar(false);
		}, 5000);
		const pronto = () => {
			if (!this.listo && !this.ocupado && !this.bloqueado && document.visibilityState === "visible" && Date.now() - this.ultimoReinicio > 10000) this.reiniciar(false);
		};
		window.addEventListener("online", pronto);
		document.addEventListener("visibilitychange", () => { if (this.noListoDesde && Date.now() - this.noListoDesde > 5000) pronto(); });
	}

	/** Tira la conexión (si la hay) y vuelve a registrar la extensión. Nunca durante una llamada. */
	reiniciar(pedirMicro: boolean, aMano = pedirMicro): Promise<void> {
		if (this.ocupado) return Promise.resolve();
		if (this.reiniciando) return this.reiniciando;
		if (aMano) { this.bloqueado = false; this.fallas = 0; }
		else if (this.bloqueado) return Promise.resolve();
		this.ultimoReinicio = Date.now();
		this.reiniciando = (async () => {
			try {
				const creds = this.fuente ? await this.fuente() : this.creds;
				if (!aMano) this.fallas = Math.min(this.fallas + 1, 6);
				if (!creds || this.ocupado) return;
				this.soltar();
				await this.conectar(creds, pedirMicro);
			} catch (e: any) {
				// 4xx = el servidor dice que no (extensión apagada, sin extensiones libres): reintentar solo no lo arregla y
				// cada 60 s por persona era un sondeo eterno. Se queda en error con el motivo hasta que den clic.
				const st = Number(e?.response?.status || 0);
				const no = st >= 400 && st < 500 && st !== 408 && st !== 429;
				if (no) this.bloqueado = true;
				this.estado = "error";
				this.error = (no && e?.response?.data?.message) || "No se pudo conectar el teléfono. Reintentando…";
				this.avisar();
			} finally { this.reiniciando = null; }
		})();
		return this.reiniciando;
	}

	/**
	 * Llamar aunque el teléfono se esté reconectando: lo reconecta (si hace falta) y marca en cuanto queda listo.
	 * Devuelve null si en `ms` no quedó listo o ya hay una llamada.
	 */
	async llamarYa(number: string, info: Parameters<Telefono["llamar"]>[1] = {}, ms = 15000): Promise<string | null> {
		if (this.ocupado) return null;
		this.ctxAudio();   // con el clic (iPhone)
		if (!this.listo) {
			if (!this.reiniciando && (this.estado !== "conectando" || Date.now() - this.ultimoReinicio > 8000)) this.reiniciar(false, true);
			const ok = await new Promise<boolean>((resolve) => {
				const t = window.setTimeout(() => { off(); resolve(this.listo); }, ms);
				// Si el servidor dijo que no (extensión apagada), no tiene caso esperar los 15 s.
				const off = this.suscribir(() => { if (this.listo || this.bloqueado) { window.clearTimeout(t); off(); resolve(this.listo); } });
			});
			if (!ok) return null;
		}
		return this.llamar(number, info);
	}

	get listo(): boolean { return this.estado === "listo"; }
	get ocupado(): boolean { return !!this.llamada && this.llamada.status !== "terminada"; }

	/**
	 * Encender: registra la extensión. Al encenderlo a mano se pide el micrófono una vez; al reconectar solo (cada vez
	 * que entra a CRM/WappChat) ya no: el micrófono se pide hasta la llamada. En iPhone, Safari volvía a preguntar
	 * cada vez que entraba a la sección (Esteban, 2026-10-09).
	 */
	async conectar(creds: PbxCreds, pedirMicro = true): Promise<void> {
		if (this.armando || (this.ua && (this.estado === "listo" || this.estado === "conectando"))) return;
		this.armando = true;
		try { await this.armar(creds, pedirMicro); } finally { this.armando = false; }
	}

	private armando = false;

	private async armar(creds: PbxCreds, pedirMicro: boolean): Promise<void> {
		this.creds = creds;
		this.estado = "conectando"; this.error = null; this.avisar();
		// Permiso de avisos con el mismo clic de Encender (para las entrantes con WAPP minimizada).
		// Una vez por página: el vigilante reconecta solo y no debe volver a sacar el aviso del navegador en cada vuelta.
		if (!this.avisosPedidos) {
			this.avisosPedidos = true;
			try { if ("Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => null); } catch (e) { /* sin avisos */ }
		}
		if (pedirMicro) {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
				stream.getTracks().forEach((t) => t.stop());   // solo para pedir permiso una vez
			} catch (e) {
				this.estado = "error"; this.error = "El navegador no dio permiso al micrófono."; this.avisar();
				return;
			}
		}
		let JsSIP: any;
		try { JsSIP = await cargarJsSIP(); } catch (e) { this.estado = "error"; this.error = "No cargó el teléfono. Recarga la página."; this.avisar(); return; }
		this.audio = this.audio || Object.assign(document.createElement("audio"), { autoplay: true });
		const socket = new JsSIP.WebSocketInterface(creds.wss);
		const ua = new JsSIP.UA({
			sockets: [socket], uri: `sip:${creds.extension}@${creds.domain}`, password: creds.password,
			display_name: creds.name, register: creds.registrar !== false, session_timers: false, connection_recovery_min_interval: 2, connection_recovery_max_interval: 30,
		});
		this.ua = ua;
		// Lo que avise una conexión vieja (ya reemplazada por el vigilante) no toca el estado de la nueva.
		const mia = () => this.ua === ua;
		// Sin registro (entrada compartida de WappTeam): listo en cuanto conecta con la central.
		if (creds.registrar === false) ua.on("connected", () => { if (!mia()) return; this.reintentos = 0; this.fallas = 0; this.estado = "listo"; this.error = null; this.avisar(); });
		ua.on("registered", () => { if (!mia()) return; this.reintentos = 0; this.fallas = 0; this.estado = "listo"; this.error = null; this.avisar(); });
		ua.on("unregistered", () => { if (mia() && this.estado === "listo") { this.estado = "conectando"; this.avisar(); } });
		ua.on("registrationFailed", (e: any) => {
			if (!mia()) return;
			// Extensión recién creada: la central baja la lista de WAPP cada minuto y mientras tanto no la conoce
			// (Denise, 2026-10-09: "Authentication Error" al encender por primera vez). Se reintenta 90 s sin asustar.
			if (this.reintentos < 9) {
				this.reintentos++; this.estado = "conectando"; this.error = null; this.avisar();
				clearTimeout(this.tReintento);
				this.tReintento = setTimeout(() => { try { if (mia()) ua.register(); } catch (x) { /* se reemplazó */ } }, 10000);
				return;
			}
			this.estado = "error"; this.error = "La central no aceptó la extensión (" + (e?.cause || "sin respuesta") + "). Reintentando…"; this.avisar();
		});
		ua.on("disconnected", () => { if (mia() && this.estado !== "apagado") { this.estado = "conectando"; this.avisar(); } });
		ua.on("newRTCSession", (d: any) => { if (mia()) this.nuevaSesion(d); });
		ua.start();
	}

	/** Cierra la conexión actual (solo para reconectar: el teléfono ya no se apaga a mano). */
	private soltar() {
		clearTimeout(this.tReintento); this.reintentos = 0;
		const ua = this.ua;
		this.ua = null; this.estado = "apagado";
		try { ua?.stop(); } catch (e) { /* ya estaba cerrado */ }
		this.avisar();
	}

	private opciones() {
		return { mediaConstraints: { audio: true, video: false }, pcConfig: { iceServers: this.creds?.iceServers || [] }, rtcOfferConstraints: { offerToReceiveAudio: true } };
	}

	private conectarAudio(session: any) {
		const pc: RTCPeerConnection | undefined = session.connection;
		if (!pc || !this.audio) return;
		const poner = () => {
			const recv = pc.getReceivers().map((r) => r.track).filter(Boolean) as MediaStreamTrack[];
			// Saliente sin contestar: el audio del operador va callado (ring y mensajes se cruzaban con el ring propio).
			if (recv.length && this.audio) { this.audio.muted = !!(this.llamada && this.llamada.direction === "out" && !this.llamada.startedAt); this.audio.srcObject = new MediaStream(recv); this.audio.play().catch(() => { /* lo reanuda el siguiente clic */ }); }
		};
		pc.addEventListener("track", poner);
		poner();
	}

	/**
	 * JsSIP espera a revisar todas las rutas de red (ICE) antes de mandar la llamada: en celular eran ~15 s de "Marcando"
	 * (Esteban, 2026-10-09). Sale en cuanto hay una ruta pública (srflx) o al segundo, lo que pase primero.
	 */
	private salirPronto(s: any) {
		let listo = false; let t: any = null;
		const ya = (e: any) => { if (!listo) { listo = true; clearTimeout(t); try { e.ready(); } catch (x) { /* ya salió */ } } };
		s.on("icecandidate", (e: any) => {
			if (!t) t = setTimeout(() => ya(e), 1000);
			if (e?.candidate?.type === "srflx" || / typ srflx /.test(String(e?.candidate?.candidate || ""))) ya(e);
		});
	}

	/** El AudioContext se crea con el clic (Llamar / Contestar): iPhone no deja sonar audio que no nació de un toque. */
	private ctxAudio(): AudioContext | null {
		try {
			const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
			if (!(this as any)._ctx || (this as any)._ctx.state === "closed") (this as any)._ctx = new Ctx();
			(this as any)._ctx.resume?.();
			return (this as any)._ctx;
		} catch (e) { return null; }
	}

	/**
	 * Tonos sintéticos (sin archivos). "entrante": dos tonos cortos repetidos. "ring": el tono de llamada saliente de
	 * Latinoamérica (425 Hz, 1 s sí / 4 s no) mientras el otro lado timbra (Esteban, 2026-10-09: "no escucho el ring").
	 * Si la compañía manda su propio audio antes de contestar (el timbre real o "el número que usted marcó…"), ese manda y
	 * el tono sintético se calla: ver vigilarAudioAdelantado.
	 */
	/** WAV de tono de llamada latinoamericano (425 Hz, 1 s sí / 4 s no), generado una vez, para un <audio> en bucle. */
	private static wavRing: string | null = null;
	private static ringUrl(): string {
		if (Telefono.wavRing) return Telefono.wavRing;
		const sr = 8000, total = sr * 5, on = sr * 1;
		const buf = new ArrayBuffer(44 + total * 2); const v = new DataView(buf);
		const w = (o: number, t: string) => { for (let i = 0; i < t.length; i++) v.setUint8(o + i, t.charCodeAt(i)); };
		w(0, "RIFF"); v.setUint32(4, 36 + total * 2, true); w(8, "WAVE"); w(12, "fmt "); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
		v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); w(36, "data"); v.setUint32(40, total * 2, true);
		for (let i = 0; i < total; i++) v.setInt16(44 + i * 2, i < on ? Math.round(Math.sin((2 * Math.PI * 425 * i) / sr) * 6000) : 0, true);
		Telefono.wavRing = URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
		return Telefono.wavRing;
	}

	/**
	 * "Conectando" (Esteban, 2026-10-09: "algún sonido para que no parezca que está fallando… tipo digital"; eligió el
	 * "latido digital" menos frecuente): dos pulsos cortos y suaves (520 y 620 Hz) cada 3 s, mientras la compañía no avisa
	 * que el otro teléfono ya timbra. Igual a docs/sonidos/conectando_latido.wav.
	 */
	private static wavConectando: string | null = null;
	private static conectandoUrl(): string {
		if (Telefono.wavConectando) return Telefono.wavConectando;
		const sr = 22050, total = Math.round(sr * 3);
		const vals = new Float32Array(total);
		const pulso = (f: number, ini: number) => {
			for (let i = Math.round(ini * sr); i < Math.min(total, Math.round((ini + 0.12) * sr)); i++) {
				const u = i / sr - ini;
				const env = Math.min(1, u / 0.005) * Math.exp(-u / 0.04);
				vals[i] += env * (Math.sin(2 * Math.PI * f * u) + 0.4 * Math.sin(2 * Math.PI * f * 2 * u));
			}
		};
		pulso(520, 0); pulso(620, 0.16);
		let pico = 0;
		for (let i = 0; i < total; i++) pico = Math.max(pico, Math.abs(vals[i]));
		const buf = new ArrayBuffer(44 + total * 2); const v = new DataView(buf);
		const w = (o: number, t: string) => { for (let i = 0; i < t.length; i++) v.setUint8(o + i, t.charCodeAt(i)); };
		w(0, "RIFF"); v.setUint32(4, 36 + total * 2, true); w(8, "WAVE"); w(12, "fmt "); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
		v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); w(36, "data"); v.setUint32(40, total * 2, true);
		for (let i = 0; i < total; i++) v.setInt16(44 + i * 2, Math.round((vals[i] / (pico || 1)) * 8000), true);
		Telefono.wavConectando = URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
		return Telefono.wavConectando;
	}

	/**
	 * "entrante": dos tonos cortos repetidos (Web Audio). "ring": el tono de llamada saliente mientras no contestan
	 * (Esteban, 2026-10-09: "no escucho el ring"). Va en un <audio> y arranca con el toque de Llamar: el iPhone silencia
	 * Web Audio con el interruptor de silencio y no deja sonar audio que no nació de un toque.
	 */
	private sonar(on: boolean, tipo: "entrante" | "ring" | "conectando" = "entrante") {
		const actual = (this as any)._timbre;
		if (actual) { clearInterval(actual.t); try { actual.o?.stop(); actual.o?.disconnect(); } catch (e) { /* ya parado */ } (this as any)._timbre = null; }
		const ring: HTMLAudioElement | undefined = (this as any)._ring;
		if (ring && (!on || tipo === "entrante")) { ring.pause(); ring.currentTime = 0; }
		if (!on) return;
		if (tipo === "ring" || tipo === "conectando") {
			// Un solo <audio> para los dos: nace con el toque de Llamar (iPhone) y después solo cambia de sonido.
			try {
				const url = tipo === "ring" ? Telefono.ringUrl() : Telefono.conectandoUrl();
				const a: HTMLAudioElement = ring || new Audio(url);
				if (a.src !== url) { a.src = url; a.currentTime = 0; }
				a.loop = true; a.volume = tipo === "ring" ? 0.6 : 0.5; (this as any)._ring = a;
				a.play().catch(() => { /* sin sonido: queda el estado en pantalla */ });
			} catch (e) { /* sin sonido */ }
			return;
		}
		const ctx = this.ctxAudio();
		if (!ctx) return;
		try {
			const o = ctx.createOscillator(); const g = ctx.createGain();
			o.frequency.value = 440; o.connect(g); g.connect(ctx.destination); g.gain.value = 0;
			o.start();
			const pulso = () => { const t0 = ctx.currentTime; g.gain.setValueAtTime(0.08, t0); g.gain.setValueAtTime(0, t0 + 0.4); };
			pulso();
			(this as any)._timbre = { o, t: setInterval(pulso, 1500) };
		} catch (e) { /* sin timbre: queda el aviso en pantalla */ }
	}

	private nuevaSesion(d: any) {
		const s = d.session;
		if (d.originator === "remote") {
			if (this.ocupado) { s.terminate({ status_code: 486 }); return; }   // ocupada: la central la manda a otra del equipo
			const num = String(s.remote_identity?.uri?.user || "");
			this.session = s;
			this.llamada = { callId: s.request?.getHeader?.("X-WA-Call") || uuid(), direction: "in", number: num, name: s.remote_identity?.display_name || null,
				prospectId: null, status: "entrante", startedAt: null, muted: false, held: false, consult: null };
			this.campaignContactId = null;
			this.sonar(true);
			this.engancharSesion(s);
			this.avisar();
			this.avisoSistema(s, this.llamada.name || num);
		}
	}

	/** WAPP minimizada o en otra pestaña: aviso del sistema que se queda hasta contestar o colgar. Clic = volver a WAPP. */
	private avisoSistema(s: any, quien: string) {
		if (!document.hidden) return;
		avisoLocal("Llamada entrante", quien, "llamada", "/");   // mismo tag que el push de la central
		const cerrar = () => {
			if (!("serviceWorker" in navigator)) return;
			navigator.serviceWorker.getRegistration("/").then((r) => r?.getNotifications({ tag: "llamada" })).then((ns) => ns?.forEach((n) => n.close())).catch(() => null);
		};
		s.on("accepted", cerrar); s.on("ended", cerrar); s.on("failed", cerrar);
	}

	private engancharSesion(s: any) {
		s.on("peerconnection", () => this.conectarAudio(s));
		// La compañía avisa que el otro teléfono ya timbra: de "conectando" al timbre.
		s.on("progress", (e: any) => {
			if (!this.llamada || this.llamada.direction !== "out") return;
			if (this.llamada.status !== "sonando") this.sonar(true, "ring");
			this.llamada.status = "sonando"; this.avisar();
			if (e?.response?.body) this.vigilarAudioAdelantado(s);
		});
		s.on("accepted", () => { if (this.audio) this.audio.muted = false; if (this.llamada) { this.llamada.status = "en_llamada"; this.llamada.startedAt = Date.now(); this.sonar(false); this.avisar(); } });
		s.on("confirmed", () => this.conectarAudio(s));
		s.on("hold", () => { if (this.llamada) { this.llamada.held = true; this.avisar(); } });
		s.on("unhold", () => { if (this.llamada) { this.llamada.held = false; this.avisar(); } });
		const fin = (e: any) => this.terminar(s, e?.cause || null, motivoFallo(e?.cause || null, e?.message?.status_code || null, e?.originator || null));
		s.on("ended", fin);
		s.on("failed", fin);
	}

	/**
	 * Audio adelantado de la compañía (183 con audio). Antes de 2026-10-09 la asesora oía solo nuestro timbre aunque el
	 * celular estuviera apagado o el número no existiera: la compañía lo decía en una grabación que nadie escuchaba, y se
	 * sentía como "suena y nadie contesta". En cuanto llega sonido real se calla el tono sintético y se oye lo que pone la
	 * compañía, como en cualquier teléfono.
	 */
	private vigilarAudioAdelantado(s: any) {
		if ((s as any)._vigilado) return;
		const pc: RTCPeerConnection | undefined = s.connection;
		const ctx = this.ctxAudio();
		if (!pc || !ctx) return;
		const tracks = pc.getReceivers().map((r) => r.track).filter((t) => t && t.kind === "audio") as MediaStreamTrack[];
		if (!tracks.length) return;
		let an: AnalyserNode;
		try { const src = ctx.createMediaStreamSource(new MediaStream(tracks)); an = ctx.createAnalyser(); an.fftSize = 512; src.connect(an); } catch (e) { return; }
		(s as any)._vigilado = true;
		this.conectarAudio(s);
		const buf = new Float32Array(an.fftSize);
		// Siempre se oye algo mientras timbra (Esteban, 2026-10-13: "no suena ni siquiera el ring"): antes el tono propio se
		// callaba con cualquier ruido de la compañía y el audio de la compañía iba silenciado (conectarAudio), así que
		// quedaba silencio. Ahora: con 0.8 s seguidos de sonido real se oye la compañía (su timbre o su grabación) y se
		// calla el propio; si la compañía se queda callada 5 s, vuelve el tono propio y la compañía se silencia otra vez.
		let sonido = 0;
		let silencio = 0;
		let compania = false;
		const t = setInterval(() => {
			if (s !== this.session || !this.llamada || this.llamada.status !== "sonando") { clearInterval(t); return; }
			an.getFloatTimeDomainData(buf);
			let suma = 0;
			for (let i = 0; i < buf.length; i++) suma += buf[i] * buf[i];
			if (Math.sqrt(suma / buf.length) > 0.01) { sonido++; silencio = 0; } else { silencio++; sonido = 0; }
			if (!compania && sonido >= 8) {
				compania = true;
				this.sonar(false);
				if (this.audio) this.audio.muted = false;
			} else if (compania && silencio >= 50) {
				compania = false;
				if (this.audio) this.audio.muted = true;
				this.sonar(true, "ring");
			}
		}, 100);
	}

	private terminar(s: any, cause: string | null, motivo: string | null = null) {
		if (s !== this.session || !this.llamada) return;
		this.sonar(false);
		const l = this.llamada;
		const f: FinLlamada = { callId: l.callId, prospectId: l.prospectId, number: l.number, direction: l.direction,
			durationSec: l.startedAt ? Math.round((Date.now() - l.startedAt) / 1000) : 0, answered: !!l.startedAt, cause, campaignContactId: this.campaignContactId, equipo: l.equipo || null };
		// Sin contestar y con motivo: el aviso se queda unos segundos; si no, el panel se va enseguida.
		const avisar = !l.startedAt && motivo && !l.equipo;
		this.llamada = { ...l, status: "terminada", motivo: avisar ? motivo : null };
		if (this.audio) this.audio.muted = false;
		this.session = null;
		if (this.consultSession) { try { this.consultSession.terminate(); } catch (e) { /* ya colgó */ } this.consultSession = null; }
		this.avisar();
		this.finales.forEach((fn) => { try { fn(f); } catch (e) { /* sigue con las demás */ } });
		const callId = l.callId;
		setTimeout(() => { if (this.llamada?.status === "terminada" && this.llamada.callId === callId) { this.llamada = null; this.avisar(); } }, avisar ? 7000 : 400);
	}

	/** Cerrar el aviso de "no se pudo completar". */
	cerrarAviso() {
		if (this.llamada?.status === "terminada") { this.llamada = null; this.avisar(); }
	}

	/** Marcar. number = lo marcable (50433277716) o una extensión. */
	llamar(number: string, info: { prospectId?: number | null; name?: string | null; campaignContactId?: number | null; campaignId?: number | null; callerId?: string | null; equipo?: number | null } = {}): string | null {
		if (!this.ua || !this.listo || this.ocupado || !this.creds) return null;
		this.ctxAudio();
		// WappTeam pone su propio tono (la sala contesta al instante y el timbre depende de si las demás contestan).
		if (!info.equipo) this.sonar(true, "conectando");   // arranca con el toque (iPhone); pasa a timbre al sonar y se apaga al contestar, colgar o fallar
		const callId = uuid();
		const headers = ["X-WA-Call: " + callId];
		if (info.prospectId) headers.push("X-WA-Prospect: " + info.prospectId);
		// La central guarda la campaña en el historial y, si la campaña tiene su propio número, lo muestra al cliente.
		if (info.campaignId) headers.push("X-WA-Campaign: " + info.campaignId);
		if (info.callerId) headers.push("X-WA-CID: " + String(info.callerId).replace(/\D/g, ""));
		this.llamada = { callId, direction: "out", number, name: info.name || null, prospectId: info.prospectId || null, status: "marcando",
			startedAt: null, muted: false, held: false, consult: null, equipo: info.equipo || null };
		this.campaignContactId = info.campaignContactId || null;
		const s = this.ua.call(`sip:${number}@${this.creds.domain}`, { ...this.opciones(), extraHeaders: headers });
		this.salirPronto(s);
		this.session = s;
		this.engancharSesion(s);
		// "peerconnection" ya pasó dentro de ua.call(): sin esto el audio de la compañía no se oía hasta contestar.
		this.conectarAudio(s);
		this.avisar();
		return callId;
	}

	contestar() {
		if (!this.session || this.llamada?.status !== "entrante") return;
		this.sonar(false);
		this.session.answer(this.opciones());
	}

	/** Pegar la persona que llama a su ficha (al abrirla desde el aviso de llamada entrante). */
	ligar(prospectId: number, name: string | null) {
		if (this.llamada) { this.llamada = { ...this.llamada, prospectId, name: name || this.llamada.name }; this.avisar(); }
	}

	colgar() {
		if (this.consultSession) { try { this.consultSession.terminate(); } catch (e) { /* ya */ } this.consultSession = null; }
		try { this.session?.terminate(); } catch (e) { /* ya colgó */ }
	}

	silenciar() {
		if (!this.session || !this.llamada) return;
		if (this.llamada.muted) this.session.unmute({ audio: true }); else this.session.mute({ audio: true });
		this.llamada = { ...this.llamada, muted: !this.llamada.muted }; this.avisar();
	}

	espera() {
		if (!this.session || !this.llamada) return;
		if (this.llamada.held) this.session.unhold(); else this.session.hold();
	}

	tecla(d: string) { try { this.session?.sendDTMF(d, { duration: 120, interToneGap: 70 }); } catch (e) { /* sin llamada */ } }

	/** Transferencia directa: el cliente pasa a esa extensión (o equipo 7xx) y esta asesora queda libre. */
	transferir(destino: string) {
		if (!this.session || !this.creds) return;
		this.session.refer(`sip:${destino}@${this.creds.domain}`);
	}

	/** Con aviso: el cliente queda en espera, se llama a la compañera; al completar, el cliente pasa con ella. */
	consultar(destino: string) {
		if (!this.session || !this.creds || !this.llamada || this.consultSession) return;
		if (!this.llamada.held) this.session.hold();
		const c = this.ua.call(`sip:${destino}@${this.creds.domain}`, this.opciones());
		this.consultSession = c;
		this.llamada = { ...this.llamada, consult: { number: destino, status: "marcando" } }; this.avisar();
		c.on("peerconnection", () => this.conectarAudio(c));
		c.on("accepted", () => { if (this.llamada?.consult) { this.llamada = { ...this.llamada, consult: { ...this.llamada.consult, status: "en_llamada" } }; this.avisar(); } });
		const fin = () => {
			if (this.consultSession !== c) return;
			this.consultSession = null;
			if (this.llamada) { this.llamada = { ...this.llamada, consult: null }; this.avisar(); }
			if (this.session) this.conectarAudio(this.session);
		};
		c.on("ended", fin); c.on("failed", fin);
	}

	completarTransferencia() {
		if (!this.session || !this.consultSession || !this.creds || !this.llamada?.consult) return;
		this.session.refer(`sip:${this.llamada.consult.number}@${this.creds.domain}`, { replaces: this.consultSession });
	}

	cancelarConsulta() {
		if (this.consultSession) { try { this.consultSession.terminate(); } catch (e) { /* ya */ } }
		if (this.session && this.llamada?.held) this.session.unhold();
	}
}

export const telefono = new Telefono();
// Una nueva versión de WAPP no recarga la pestaña mientras suena o dura una llamada (recargar la colgaría).
frenarRecarga(() => !!telefono.llamada && telefono.llamada.status !== "terminada");
